'use client';

import { useGlobalLabels } from '@/context/GlobalLabelContext';
import PlainTextWrapper from '@/helpers/Wrappers/PlainTextWrapper/PlainTextWrapper';
import { LanguageSelector } from '../LanguageSelector/LanguageSelector';
import { desktopHeaderVariants } from './Header.styles';
import { cn } from 'tailwind-variants';
import { trackAdobeClick } from '@/utils/adobe/click';

interface MobileNavProps {
  mobileMenuOpen: boolean;
  mobileSearchOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  setMobileSearchOpen: (open: boolean) => void;
  entryUid?: string;
  contentTypeUid?: string;
  hasAlternateLanguage?: boolean;
  englishUrl?: string;
  spanishUrl?: string;
}

export const MobileNav = ({
  mobileMenuOpen,
  mobileSearchOpen,
  setMobileMenuOpen,
  setMobileSearchOpen,
  entryUid,
  contentTypeUid,
  hasAlternateLanguage,
  englishUrl,
  spanishUrl,
}: MobileNavProps) => {
  const { globalLabels } = useGlobalLabels();
  const {
    mobileNav,
    mobileMenuToggle,
    hamburgerIcon,
    menuLabel,
    toggleIcon,
    toggleIconStack,
    menuIconBars,
    menuIconClose,
    mobileSearchIcon,
  } = desktopHeaderVariants({
    menuVisible: false,
    mobileMenuOpen,
  });

  return (
    <nav className={mobileNav()}>
      {/* Mobile Search  */}
      <button
        className={mobileMenuToggle()}
        onClick={() => {
          trackAdobeClick({ clickName: globalLabels.search_labels?.search_label ?? 'Search', clickType: 'button', clickLocation: 'mobileNav' });
          setMobileSearchOpen(!mobileSearchOpen);
        }}
        aria-expanded={mobileSearchOpen}
        aria-label={globalLabels.search_labels?.search_label || 'Search'}
      >
        <i className={cn(toggleIcon(), mobileSearchIcon())} />
        <PlainTextWrapper
          content={globalLabels.search_labels?.search_label}
          cslpAttribute={globalLabels.search_labels?.$?.search_label}
          className={menuLabel()}
        />
      </button>
      <LanguageSelector
        variant="mobile"
        entryUid={entryUid}
        contentTypeUid={contentTypeUid}
        hasAlternateLanguage={hasAlternateLanguage}
        englishUrl={englishUrl}
        spanishUrl={spanishUrl}
      />
      <button
        data-mobile-menu-toggle
        className={mobileMenuToggle()}
        onClick={() => {
          trackAdobeClick({ clickName: globalLabels.menu_label ?? 'Menu', clickType: 'button', clickLocation: 'mobileNav' });
          setMobileMenuOpen(!mobileMenuOpen);
        }}
        aria-expanded={mobileMenuOpen}
        aria-label={globalLabels.menu_label || 'Menu'}
      >
        {/* Both glyphs stay mounted and cross-fade, so the swap animates instead of popping */}
        <span className={toggleIconStack()} aria-hidden="true">
          <i className={cn(hamburgerIcon(), menuIconBars(), 'fa-solid fa-bars')} />
          <i className={cn(hamburgerIcon(), menuIconClose(), 'fa-solid fa-xmark')} />
        </span>
        <PlainTextWrapper
          content={globalLabels.menu_label}
          cslpAttribute={globalLabels.$?.menu_label}
          className={menuLabel()}
        />
      </button>
    </nav>
  );
};
